import {
  Box,
  Checkbox,
  Dialog,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { icons } from "../../../stack/environment/icons/iconExport";

const SaveViewModal = ({
  open,
  onClose,
  onSave,
  existingView = null,
  isLoading = false,
}) => {
  const colors = useColors();
  const [viewName, setViewName] = useState("");
  const [saveQueryAndFilters, setSaveQueryAndFilters] = useState(true);
  const [privacy, setPrivacy] = useState("public");
  const [showAsterisk, setShowAsterisk] = useState(true);

  useEffect(() => {
    setShowAsterisk(viewName.trim() === "");
  }, [viewName]);

  useEffect(() => {
    if (existingView) {
      setViewName(existingView.name || "");
      setPrivacy(existingView.is_public ? "public" : "private");
    } else {
      setViewName("");
      setPrivacy("public");
      setSaveQueryAndFilters(true);
    }
  }, [existingView, open]);

  const handleSave = async () => {
    if (viewName.trim()) {
      await onSave({
        view_id: existingView?.view_id,
        name: viewName.trim(),
        save_query_and_filters: saveQueryAndFilters,
        is_public: privacy === "public",
      });
      if (!existingView) {
        setViewName("");
        setPrivacy("public");
        setSaveQueryAndFilters(true);
        setShowAsterisk(true);
      }
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setViewName("");
      setPrivacy("public");
      setSaveQueryAndFilters(true);
      setShowAsterisk(true);
      onClose();
    }
  };

  const isFormValid = viewName.trim() !== "";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.modal_bg,
          borderRadius: "10px",
          boxShadow: colors.dropdown_menu_shadow,
          width: "379px",
          maxWidth: "379px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "24px 24px 16px 24px",
          color: colors.text_primary,
          lineHeight: "22px",
        }}
      >
        <Typography variant="subtitle1">
          {existingView ? "Edit View" : "Save View"}
        </Typography>
      </DialogTitle>

      <Box sx={{ padding: "0 24px 24px 24px" }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Box sx={{ position: "relative" }}>
            <TextField
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              placeholder="Add a view name"
              fullWidth
              variant="outlined"
              inputProps={{ maxLength: 50 }}
              error={!isFormValid && showAsterisk}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.common_white,
                  minHeight: "44px",
                  paddingLeft: "14px",
                  paddingRight: "8px",
                  paddingTop: "8px",
                  paddingBottom: "8px",
                  borderRadius: "8px",
                  "& fieldset": {
                    border: `1px solid ${
                      !isFormValid && showAsterisk
                        ? colors.error_main
                        : colors.textfield_border
                    }`,
                    borderRadius: "8px",
                  },
                  "&:hover fieldset": {
                    borderColor: colors.textfield_border_hover,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.primary_main,
                  },
                },
                "& .MuiInputBase-input": {
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "16px",
                  color: colors.text_secondary,
                  padding: 0,
                },
                "& .MuiInputBase-input::placeholder": {
                  color: colors.text_secondary,
                  opacity: 1,
                },
              }}
            />
            {showAsterisk && (
              <Typography
                sx={{
                  position: "absolute",
                  right: "14px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "14px",
                  color: colors.error_main,
                  fontWeight: 400,
                  pointerEvents: "none",
                }}
              >
                *
              </Typography>
            )}
          </Box>
          <Box display="flex" flexDirection="column">
            <FormControlLabel
              disabled={true}
              control={
                <Checkbox
                  checked={true}
                  onChange={(e) => {}}
                  sx={{
                    color: colors.primary_main,
                    "&.Mui-checked": {
                      color: colors.primary_main,
                    },
                  }}
                  icon={
                    <SvgToIcon
                      IconLogo={icons.Unchecked}
                      style={{ width: "20px", height: "20px" }}
                    />
                  }
                  checkedIcon={
                    <SvgToIcon
                      IconLogo={icons.Checked}
                      style={{
                        width: "20px",
                        height: "20px",
                        color: colors.text_disabled,
                      }}
                    />
                  }
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text_disabled,
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "16px",
                  }}
                >
                  Save Advanced Search Query
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={saveQueryAndFilters}
                  onChange={(e) => setSaveQueryAndFilters(e.target.checked)}
                  sx={{
                    color: colors.primary_main,
                    "&.Mui-checked": {
                      color: colors.primary_main,
                    },
                  }}
                  icon={
                    <SvgToIcon
                      IconLogo={icons.Unchecked}
                      style={{ width: "20px", height: "20px" }}
                    />
                  }
                  checkedIcon={
                    <SvgToIcon
                      IconLogo={icons.Checked}
                      style={{ width: "20px", height: "20px" }}
                    />
                  }
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text_primary,
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "16px",
                  }}
                >
                  Save filters
                </Typography>
              }
            />
          </Box>
          {!existingView && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: colors.text_primary,
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: "16px",
                  mb: 1,
                  display: "block",
                }}
              >
                Privacy settings
              </Typography>
              <RadioGroup
                value={privacy}
                onChange={(e) => setPrivacy(e.target.value)}
                sx={{
                  "& .MuiRadio-root": {
                    color: colors.text_secondary,
                    "&.Mui-checked": {
                      color: colors.primary_main,
                    },
                  },
                  "&.Mui-disabled": {
                    opacity: 0.6,
                  },
                }}
              >
                <FormControlLabel
                  value="private"
                  control={<Radio size="small" />}
                  label={
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text_primary,
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "12px",
                        fontWeight: 400,
                        lineHeight: "16px",
                      }}
                    >
                      Private
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="public"
                  control={<Radio size="small" />}
                  label={
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text_primary,
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "12px",
                        fontWeight: 400,
                        lineHeight: "16px",
                      }}
                    >
                      Public
                    </Typography>
                  }
                />
              </RadioGroup>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          padding: "16px 24px 24px 24px",
          borderTop: `1px solid ${colors.grey_20percent}`,
        }}
      >
        <ContainedButton
          onClick={handleSave}
          disabled={!isFormValid || isLoading}
          loading={isLoading}
          color="primary"
          size="small"
        >
          {isLoading ? "Saving..." : "Save"}
        </ContainedButton>
      </Box>
    </Dialog>
  );
};

export default SaveViewModal;
